import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

const Contact: React.FC = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const navigate = useNavigate();

  const validate = () => {
    if (!name.trim()) return "Please enter your name.";
    if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email))
      return "Please enter a valid email.";
    if (!message.trim()) return "Please enter a message.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const v = validate();
    if (v) return setError(v);

    setLoading(true);
    try {
      await api.post("/contact", { name, email, subject, message });
      setSuccess("Thanks — your inquiry has been sent.");
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
      setTimeout(() => navigate("/"), 1500);
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <h1 className="text-3xl font-semibold mb-4 text-gray-900">Contact Us</h1>
      <p className="mb-8 text-gray-600">Send us an inquiry and we will get back to you shortly.</p>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {error && (
          <div className="col-span-full text-red-600 text-sm">{error}</div>
        )}
        {success && (
          <div className="col-span-full text-green-600 text-sm">{success}</div>
        )}

        {/* Name */}
        <div className="flex flex-col">
          <label className="text-sm font-medium mb-1">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your full name"
            className="w-full border-b border-gray-300 py-2 placeholder-gray-400 focus:outline-none focus:border-black transition"
          />
        </div>

        {/* Email */}
        <div className="flex flex-col">
          <label className="text-sm font-medium mb-1">Email</label>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full border-b border-gray-300 py-2 placeholder-gray-400 focus:outline-none focus:border-black transition"
          />
        </div>

        {/* Subject */}
        <div className="flex flex-col md:col-span-2">
          <label className="text-sm font-medium mb-1">Subject</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject (optional)"
            className="w-full border-b border-gray-300 py-2 placeholder-gray-400 focus:outline-none focus:border-black transition"
          />
        </div>

        {/* Message */}
        <div className="flex flex-col md:col-span-2">
          <label className="text-sm font-medium mb-1">Message</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Tell us about your inquiry"
            className="w-full border-b border-gray-300 py-2 placeholder-gray-400 focus:outline-none focus:border-black transition min-h-[140px] resize-none"
          />
        </div>

        {/* Submit */}
        <div className="md:col-span-2">
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-black text-white py-3 rounded-sm hover:bg-gray-800 transition disabled:opacity-50"
          >
            {loading ? "Sending…" : "Send Message"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Contact;
